/**
 * UserMonitorPage.jsx
 * Admin real-time dashboard: who is online, which page they are on,
 * session duration, and per-page grouping. Auto-refreshes every 15 seconds.
 * Follows the glassmorphic dark-mode design used across the admin section.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import {
  getOnlineUsers,
  getPresenceStats,
  getUserJourney,
} from '../../api/presenceService';

// ── Helpers ───────────────────────────────────────────────────────────────────

const REFRESH_MS = 15_000;

const formatDuration = (ms) => {
  if (!ms || ms < 0) return '0s';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const formatIdle = (ms) => {
  if (ms < 15_000) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return `${m}m ago`;
};

const formatTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const parseUserAgent = (ua = '') => {
  if (!ua || ua === 'unknown') return 'Unknown Device';
  if (/Mobile|Android/i.test(ua)) {
    if (/iPhone|iPad/i.test(ua)) return 'iOS Mobile';
    return 'Android Mobile';
  }
  if (/Edg/i.test(ua))     return 'Edge / Desktop';
  if (/Chrome/i.test(ua))  return 'Chrome / Desktop';
  if (/Firefox/i.test(ua)) return 'Firefox / Desktop';
  if (/Safari/i.test(ua))  return 'Safari / Desktop';
  return ua.substring(0, 40);
};

const ROLE_STYLES = {
  admin:   { bg: 'rgba(255,107,107,0.15)', color: '#ff6b6b', border: 'rgba(255,107,107,0.3)' },
  analyst: { bg: 'rgba(79,172,254,0.15)',  color: '#4facfe', border: 'rgba(79,172,254,0.3)' },
  viewer:  { bg: 'rgba(102,126,234,0.15)', color: '#a5b4fc', border: 'rgba(102,126,234,0.3)' },
};

const RoleBadge = ({ role }) => {
  const s = ROLE_STYLES[role] || ROLE_STYLES.viewer;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 4, padding: '2px 8px', fontSize: '0.68rem',
      fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
    }}>{role || 'viewer'}</span>
  );
};

const LiveDot = () => (
  <span style={{
    width: 8, height: 8, borderRadius: '50%',
    background: '#00f260',
    boxShadow: '0 0 8px #00f260',
    display: 'inline-block',
    animation: 'pulse 1.6s ease-in-out infinite',
  }} />
);

// ── Stat Card ─────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, gradient, icon }) => (
  <div style={{
    background: 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '1.25rem 1.5rem',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    display: 'flex', alignItems: 'center', gap: '1rem',
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12,
      background: gradient,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '1.4rem', flexShrink: 0,
      boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    }}>{icon}</div>
    <div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
        {label}
      </div>
    </div>
  </div>
);

// ── Journey Modal ─────────────────────────────────────────────────────────────

const JourneyModal = ({ userId, email, onClose }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUserJourney(userId, { limit: 300, sessionWindow: 30 })
      .then((r) => { if (!cancelled) setData(r.data.data); })
      .catch(() => { if (!cancelled) setData({ sessions: [], totalEvents: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg,#1a1a2e,#16213e)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          maxWidth: 780, width: '100%', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff' }}>
              🧭 User Journey Timeline
            </div>
            <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
              {email}
            </div>
          </div>
          <button onClick={onClose} className="action-btn"
            style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)' }}>
            ✕ Close
          </button>
        </div>

        <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto' }}>
          {loading && <div style={{ color: 'rgba(255,255,255,0.5)' }}>Loading journey…</div>}

          {!loading && (!data?.sessions?.length) && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'rgba(255,255,255,0.5)' }}>
              📭 No activity events recorded yet for this user.
            </div>
          )}

          {!loading && data?.sessions?.map((s, i) => (
            <div key={i} style={{ marginBottom: '1.5rem' }}>
              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Session {data.sessions.length - i} · {formatTime(s.startAt)} · duration{' '}
                <b style={{ color: '#00f2fe' }}>
                  {formatDuration(new Date(s.endAt) - new Date(s.startAt))}
                </b>{' '}
                · {s.events.length} events
              </div>

              <div style={{
                borderLeft: '2px solid rgba(102,126,234,0.4)',
                paddingLeft: '1.25rem',
              }}>
                {s.events.map((e) => (
                  <div key={e._id} style={{ position: 'relative', marginBottom: '0.7rem' }}>
                    <span style={{
                      position: 'absolute', left: -30, top: 5,
                      width: 10, height: 10, borderRadius: '50%',
                      background: e.status === 'failed' ? '#ff6b6b' : '#00f260',
                      boxShadow: `0 0 6px ${e.status === 'failed' ? '#ff6b6b' : '#00f260'}`,
                    }} />
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', fontFamily: 'monospace' }}>
                        {formatTime(e.createdAt)}
                      </span>
                      <span style={{
                        background: 'rgba(255,255,255,0.08)', color: '#fff',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 4, padding: '2px 8px',
                        fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em',
                      }}>{e.action}</span>
                      {e.metadata?.page && (
                        <span style={{ color: '#00f2fe', fontSize: '0.8rem' }}>→ {e.metadata.page}</span>
                      )}
                      {e.ipAddress && e.ipAddress !== 'unknown' && (
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                          {e.ipAddress}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const UserMonitorPage = () => {
  const [data, setData]           = useState(null);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [search, setSearch]       = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [journeyUser, setJourneyUser] = useState(null);
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef(null);
  const clockRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [{ data: onlineRes }, { data: statsRes }] = await Promise.all([
        getOnlineUsers(),
        getPresenceStats(),
      ]);
      setData(onlineRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Presence load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    timerRef.current = setInterval(loadAll, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [autoRefresh, loadAll]);

  // Local 1s ticker so session/idle durations count up between server polls.
  useEffect(() => {
    clockRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(clockRef.current);
  }, []);

  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];
    const q = search.trim().toLowerCase();
    return data.users.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        (u.email || '').toLowerCase().includes(q) ||
        (u.name  || '').toLowerCase().includes(q) ||
        (u.currentPage || '').toLowerCase().includes(q)
      );
    });
  }, [data, search, roleFilter]);

  const s = stats?.today || {};
  const statCards = [
    { label: 'Online Now',          value: data?.onlineCount ?? '—', gradient: 'linear-gradient(135deg,#00f260,#0575e6)', icon: '🟢' },
    { label: 'Logins Today',        value: s.LOGIN ?? 0,             gradient: 'linear-gradient(135deg,#4facfe,#00f2fe)', icon: '🔑' },
    { label: 'Failed Logins Today', value: s.FAILED_LOGIN ?? 0,      gradient: 'linear-gradient(135deg,#ff6b6b,#ee5a6f)', icon: '🚫' },
    { label: 'Admins Online',       value: data?.byRole?.admin ?? 0, gradient: 'linear-gradient(135deg,#667eea,#764ba2)', icon: '🛡️' },
  ];

  return (
    <AppLayout pageBadge="User Monitor">
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg,#1a1a2e 0%,#16213e 100%)',
        color: '#fff',
        fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
        padding: '2rem',
      }}>
        <style>{`
          @keyframes pulse {
            0%,100% { opacity: 1; transform: scale(1); }
            50%     { opacity: 0.55; transform: scale(1.25); }
          }
          .mon-row:hover { background: rgba(102,126,234,0.08) !important; }
          .filter-input {
            background: rgba(255,255,255,0.06);
            border: 1px solid rgba(255,255,255,0.12);
            border-radius: 8px;
            color: #fff;
            padding: 0.5rem 0.75rem;
            font-size: 0.85rem;
            outline: none;
          }
          .filter-input:focus { border-color: rgba(102,126,234,0.6); }
          .filter-input option { background: #1a1a2e; }
          .action-btn {
            border: none; border-radius: 8px;
            padding: 0.55rem 1.2rem;
            font-weight: 600; font-size: 0.85rem;
            cursor: pointer; transition: all 0.25s;
          }
          .action-btn:hover { transform: translateY(-1px); }
          .glass-card {
            background: rgba(255,255,255,0.04);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255,255,255,0.08);
            border-radius: 16px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          }
        `}</style>

        {/* Header */}
        <div style={{ marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <LiveDot /> User Monitor
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.55)', marginTop: 4, fontSize: '0.9rem' }}>
              Real-time view of who is online, what page they are viewing, and how long their session has been active.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (15s)
            </label>
            <button
              className="action-btn"
              onClick={loadAll}
              style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', boxShadow: '0 4px 15px rgba(102,126,234,0.3)' }}
            >
              ↻ Refresh Now
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}>
          {statCards.map((c) => <StatCard key={c.label} {...c} />)}
        </div>

        {/* Filters */}
        <div className="glass-card" style={{ padding: '1rem 1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Search</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Email, name, or page…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ minWidth: 260 }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</label>
              <select
                className="filter-input"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{ minWidth: 140 }}
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="analyst">Analyst</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <div style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem' }}>
              Showing <b style={{ color: '#fff' }}>{filteredUsers.length}</b> of {data?.users?.length ?? 0} online
            </div>
          </div>
        </div>

        {/* Two-column layout: Online users table + Page grouping */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)',
          gap: '1.5rem',
        }}>
          {/* Online users table */}
          <div className="glass-card" style={{ overflow: 'hidden' }}>
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff' }}>👥 Active Sessions</span>
              <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>
                Updated {data?.generatedAt ? formatTime(data.generatedAt) : '—'}
              </span>
            </div>

            <div style={{ overflowX: 'auto', maxHeight: 620, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 720 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr style={{ background: 'rgba(15,15,30,0.96)' }}>
                    {['User', 'Role', 'Current Page', 'Session', 'Last Seen', 'Device', ''].map((h, i) => (
                      <th key={i} style={{
                        padding: '0.9rem 1rem',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.85)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid rgba(102,126,234,0.3)',
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} style={{ padding: '3rem', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>Loading…</td></tr>
                  )}
                  {!loading && filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '4rem 1rem' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>😴</div>
                        <div style={{ color: 'rgba(255,255,255,0.5)' }}>No users online right now.</div>
                      </td>
                    </tr>
                  )}
                  {!loading && filteredUsers.map((u, idx) => {
                    const liveSession = now - new Date(u.loginAt).getTime();
                    const liveIdle    = now - new Date(u.lastSeenAt).getTime();
                    return (
                      <tr key={u._id}
                        className="mon-row"
                        style={{
                          background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                        }}
                      >
                        <td style={{ padding: '0.7rem 1rem', maxWidth: 220 }}>
                          <div style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>{u.name || '—'}</div>
                          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem' }}>{u.email}</div>
                        </td>
                        <td style={{ padding: '0.7rem 1rem' }}><RoleBadge role={u.role} /></td>
                        <td style={{ padding: '0.7rem 1rem', maxWidth: 220 }}>
                          <div style={{ color: '#00f2fe', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                            {u.currentPage}
                          </div>
                          {u.pageTitle && u.pageTitle !== u.currentPage && (
                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>{u.pageTitle}</div>
                          )}
                        </td>
                        <td style={{ padding: '0.7rem 1rem', color: '#fff', fontSize: '0.82rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {formatDuration(liveSession)}
                        </td>
                        <td style={{ padding: '0.7rem 1rem', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: liveIdle < 60_000 ? '#00f260' : 'rgba(255,255,255,0.55)' }}>
                            <LiveDot /> {formatIdle(liveIdle)}
                          </span>
                        </td>
                        <td style={{ padding: '0.7rem 1rem', color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {parseUserAgent(u.userAgent)}
                          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>{u.ipAddress}</div>
                        </td>
                        <td style={{ padding: '0.7rem 1rem' }}>
                          <button
                            className="action-btn"
                            onClick={() => setJourneyUser({ id: u.userId, email: u.email })}
                            style={{
                              background: 'rgba(102,126,234,0.15)',
                              color: '#a5b4fc',
                              border: '1px solid rgba(102,126,234,0.3)',
                              padding: '0.35rem 0.7rem',
                              fontSize: '0.75rem',
                            }}
                          >
                            🧭 Journey
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Page grouping panel */}
          <div className="glass-card" style={{ padding: '1rem 1.25rem', maxHeight: 660, overflowY: 'auto' }}>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', marginBottom: '0.75rem' }}>
              📍 Users by Page
            </div>
            {(!data?.byPage || data.byPage.length === 0) && (
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', padding: '1rem 0' }}>
                No pages active.
              </div>
            )}
            {data?.byPage?.map((group) => (
              <div key={group.page} style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 10,
                padding: '0.75rem 0.9rem',
                marginBottom: '0.6rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: '#00f2fe', fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {group.page}
                    </div>
                    {group.pageTitle && (
                      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem' }}>{group.pageTitle}</div>
                    )}
                  </div>
                  <span style={{
                    background: 'linear-gradient(135deg,#667eea,#764ba2)',
                    color: '#fff', fontWeight: 700, fontSize: '0.78rem',
                    padding: '2px 10px', borderRadius: 12,
                  }}>{group.users.length}</span>
                </div>
                <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {group.users.slice(0, 6).map((u, i) => (
                    <span key={i} title={u.email} style={{
                      background: 'rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.75)',
                      fontSize: '0.72rem',
                      padding: '2px 8px',
                      borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}>
                      {u.name || u.email.split('@')[0]}
                    </span>
                  ))}
                  {group.users.length > 6 && (
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', alignSelf: 'center' }}>
                      +{group.users.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {journeyUser && (
          <JourneyModal
            userId={journeyUser.id}
            email={journeyUser.email}
            onClose={() => setJourneyUser(null)}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default UserMonitorPage;
